import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Backdrop, CircularProgress, Grow, Modal } from '@material-ui/core';
import Dialog from '../../commons/dialog/Dialog.js';
import * as Utils from '../../commons/utils/Utils';
import backendStrings from '../../lang/backendStrings';
import { etiquetaLlavesEnVigilancia, llavesEnVigilanciaHabilitada } from './solicitudEstado.js';

const LARGO_COMENTARIO = 255;

const SolicitudRechazarModal = ({ solicitud, onClose, onRechazada }) => {
	const intl = useIntl();
	const [motivos, setMotivos] = useState([]);
	const [cargandoMotivos, setCargandoMotivos] = useState(false);
	const [motivoId, setMotivoId] = useState('');
	const [comentario, setComentario] = useState('');
	const [guardando, setGuardando] = useState(false);

	useEffect(() => {
		if (!solicitud) return undefined;

		let vigente = true;
		setMotivoId('');
		setComentario('');
		setCargandoMotivos(true);
		Utils.getData('/solicitudes/motivos-rechazo')
			.then((data) => {
				if (vigente) setMotivos(data || []);
			})
			.catch(() => {
				if (vigente) setMotivos([]);
			})
			.finally(() => {
				if (vigente) setCargandoMotivos(false);
			});

		return () => {
			vigente = false;
		};
	}, [solicitud && solicitud.id]);

	if (!solicitud) return null;

	const completo = motivoId !== '' && comentario.trim() !== '';

	const handleRechazar = () => {
		if (!completo || guardando) return;
		setGuardando(true);
		Utils.sendData('/solicitudes/' + solicitud.id + '/rechazar', 'PATCH', { motivo: parseInt(motivoId, 10), comentario: comentario })
			.then(() => {
				Dialog.alert(intl, {
					icon: 'success',
					title: intl.formatMessage({ id: 'solicitudRechazarModal.ok_titulo', defaultMessage: 'Solicitud rechazada' }),
					text: intl.formatMessage({ id: 'solicitudRechazarModal.ok_texto', defaultMessage: 'La solicitud {numero} fue rechazada.' }, { numero: solicitud.numero }),
				});
				onRechazada();
			})
			.catch(() => {})
			.finally(() => setGuardando(false));
	};

	return (
		<Modal open={!!solicitud} onClose={onClose} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 300 }}>
			<Grow in={!!solicitud}>
				<div tabIndex="-1" role="dialog">
					<div className="modal-lg modal-dialog" role="document">
						<div className="modal-content">
							<div className="modal-header bg-fleet">
								<h4 className="modal-title text-white mb-0">
									<i className="la la-times-circle align-middle icon-modal-title"></i>{' '}
									<FormattedMessage id="solicitudRechazarModal.titulo" defaultMessage="Rechazar solicitud {numero}" values={{ numero: solicitud.numero }} />
								</h4>
								<button type="button" className="close" aria-label="Close" onClick={onClose}>
									<span aria-hidden="true">&times;</span>
								</button>
							</div>
							<div className="modal-body modal-grey modal-mh ovf-x-hidden">
								<div className="card pull-up mb-0">
									<div className="card-content">
										<div className="card-body">
											<div className="form-group row">
												<label className="col-md-3 label-control col-form-label">
													<FormattedMessage id="solicitudRechazarModal.solicitud" defaultMessage="Solicitud" />:
												</label>
												<div className="col-md-9 align-self-center">
													<div className="form-control-static col-form-label form-value">
														{[
															solicitud.numero,
															solicitud.dominio,
															solicitud.unidad
																? intl.formatMessage({ id: 'solicitudRechazarModal.unidad', defaultMessage: 'Unidad: {unidad}' }, { unidad: solicitud.unidad })
																: null,
															llavesEnVigilanciaHabilitada()
																? intl.formatMessage(
																	{ id: 'solicitudRechazarModal.llaves_en_vigilancia', defaultMessage: 'Llaves en vigilancia: {valor}' },
																	{ valor: etiquetaLlavesEnVigilancia(intl, solicitud.llavesEnVigilancia) }
																)
																: null,
															solicitud.solicitante,
														]
															.filter(Boolean)
															.join(' · ')}
													</div>
												</div>
											</div>
											<div className="form-group row">
												<label className="col-md-3 label-control col-form-label" htmlFor="motivoRechazo">
													<FormattedMessage id="solicitudRechazarModal.motivo" defaultMessage="Motivo" /> *:
												</label>
												<div className="col-md-9">
													<select
														id="motivoRechazo"
														className="form-control"
														value={motivoId}
														disabled={cargandoMotivos || motivos.length === 0}
														onChange={(e) => setMotivoId(e.target.value)}
													>
														<option value="">
															{intl.formatMessage({ id: 'solicitudRechazarModal.motivo_placeholder', defaultMessage: 'Seleccione un motivo' })}
														</option>
														{motivos.map((motivo) => (
															<option key={motivo.id} value={motivo.id}>{motivo.nombre}</option>
														))}
													</select>
													{cargandoMotivos && <CircularProgress size={16} className="mt-1" />}
													{!cargandoMotivos && motivos.length === 0 ? (
														<small className="text-danger">
															<FormattedMessage
																id="solicitudRechazarModal.sin_motivos_cargados"
																defaultMessage='No hay motivos de rechazo cargados. Ingrese a Administración → Motivo y cargue uno del tipo "{tipo}".'
																values={{ tipo: intl.formatMessage(backendStrings['rechazo_solicitud']) }}
															/>
														</small>
													) : (
														<small className="text-muted">
															<FormattedMessage id="solicitudRechazarModal.motivo_ayuda" defaultMessage="Lista administrada por el cliente en el módulo de Motivos." />
														</small>
													)}
												</div>
											</div>
											<div className="form-group row">
												<label className="col-md-3 label-control col-form-label" htmlFor="comentarioRechazo">
													<FormattedMessage id="solicitudRechazarModal.comentario" defaultMessage="Comentario" /> *:
												</label>
												<div className="col-md-9">
													<textarea
														id="comentarioRechazo"
														className="form-control"
														rows={3}
														maxLength={LARGO_COMENTARIO}
														value={comentario}
														onChange={(e) => setComentario(e.target.value)}
													/>
													<small className="text-muted">{comentario.length + '/' + LARGO_COMENTARIO}</small>
												</div>
											</div>
											<div className="alert alert-info mb-0">
												<i className="la la-info-circle"></i>{' '}
												<FormattedMessage
													id="solicitudRechazarModal.aviso"
													defaultMessage='Tener en cuenta: el operador recibe una notificación y ve el motivo y el comentario en la app, en "Mis solicitudes".'
												/>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="modal-footer modal-grey">
								<button type="button" className="btn btn-primary mr-1" disabled={!completo || guardando} onClick={handleRechazar}>
									<i className="fa fa-check-circle"></i>{' '}
									<FormattedMessage id="solicitudRechazarModal.rechazar" defaultMessage="Rechazar" />
								</button>
								<button type="button" className="btn btn-danger" disabled={guardando} onClick={onClose}>
									<i className="fa fa-times-circle"></i>{' '}
									<FormattedMessage id="solicitudRechazarModal.cancelar" defaultMessage="Cancelar" />
								</button>
							</div>
						</div>
					</div>
				</div>
			</Grow>
		</Modal>
	);
};

export default SolicitudRechazarModal;
